
































































import { EmailAttachmentApi } from '@/api/email-attachment';
import { PartnerPracticeApi } from '@/api/partner-practice';
import { BadRequest } from '@/api/responses/bad-request';
import addressWithLookup from '@/components/address-with-lookup.vue';
import partnerPracticeAddressList from '@/components/settings/partner-practices/partner-practice-address-list.vue';
import partnerPracticeDetails from '@/components/settings/partner-practices/partner-practice-details.vue';
import partnerPracticeEmailList from '@/components/settings/partner-practices/partner-practice-email-list.vue';
import partnerPracticeEmail from '@/components/settings/partner-practices/partner-practice-email.vue';
import partnerPracticeOriginalSources from '@/components/settings/partner-practices/partner-practice-original-sources.vue';
import { createPartnerPractice, PartnerPractice } from '@/shared/partner-practice';
import { createPartnerPracticeAddress, PartnerPracticeAddress } from '@/shared/partner-practice-address';
import { createPartnerPracticeEmailAttachment, PartnerPracticeEmailAttachment } from '@/shared/partner-practice-email-attachment';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

const emailAttachmentApi = new EmailAttachmentApi();
const partnerPracticeApi = new PartnerPracticeApi();

@Component({
    components: {
        addressWithLookup,
        partnerPracticeAddressList,
        partnerPracticeDetails,
        partnerPracticeEmailList,
        partnerPracticeEmail,
        partnerPracticeOriginalSources,
    },
})
export default class AddPartnerPracticeDialog extends Vue {
    @Prop({ required: true }) private visible!: boolean;

    private isVisible: boolean = this.visible;
    private isSaving: boolean = false;
    private currentStep: number = 0;
    private maxSteps: number = 3;
    private detailsCompleted: boolean = false;
    private addressesCompleted: boolean = false;
    private introducersCompleted: boolean = false;
    private partnerPractice: PartnerPractice = createPartnerPractice();

    private isAddressVisible: boolean = false;
    private isAddressEdit: boolean = false;
    private selectedAddress: PartnerPracticeAddress | null = null;

    private isEmailVisible: boolean = false;
    private isEmailEdit: boolean = false;
    private importEmailAttachment: boolean = false;
    private emailAttachmentFile: File | null = null;
    private selectedEmailAttachment: PartnerPracticeEmailAttachment | null = null;

    private get allowNext(): boolean {
        if (this.currentStep !== 1) {
            return true;
        }

        return this.partnerPractice.addresses.length > 0;
    }

    @Watch('visible')
    private onVisibleChanged(visible: boolean): void {
        this.isVisible = visible;
    }

    private async onNextAsync(): Promise<void> {
        switch (this.currentStep) {
            case 0:
                if (!(await (this.$refs.details as Vue).$validator.validateAll('partner-practice-details'))) {
                    return;
                }

                this.detailsCompleted = true;
                break;

            case 1:
                this.addressesCompleted = true;
                break;

            case 2:
                this.introducersCompleted = true;
                break;
        }

        setTimeout(() => { this.currentStep++; }, 0);
    }

    private async onAddAsync(): Promise<void> {
        this.isSaving = true;

        try {
            await partnerPracticeApi.createPartnerPracticeAsync({
                ...this.partnerPractice,
                addresses: [...this.partnerPractice.addresses],
                emailAttachments: [...this.partnerPractice.emailAttachments],
                originalSourceTypes: [...this.partnerPractice.originalSourceTypes],
            });

            this.$emit('ok', { ...this.partnerPractice });
        } catch (e) {
            if (e instanceof Error) {
                this.$snackbar.open({ message: e.message, type: 'is-danger' });
            } else {
                this.$snackbar.open({ message: `Could not create partner practice '${this.partnerPractice.name}'. Please try again.`, type: 'is-danger' });
            }
        }

        this.isSaving = false;
    }

    private onPrevious(): void {
        this.currentStep--;
    }

    private onCancel(): void {
        this.$emit('cancel');
    }

    private async onSaveAddressAsync(): Promise<void> {
        if (!(await (this.$refs.address as Vue).$validator.validateAll('address-details'))) {
            return;
        }

        this.isSaving = true;

        if (this.isAddressEdit) {
            const index = this.partnerPractice.addresses.findIndex((address) => address.id === this.selectedAddress!.id!);

            if (index > -1) {
                if (this.selectedAddress!.id === 'editing') {
                    this.selectedAddress!.id = null;
                }

                this.partnerPractice.addresses.splice(index, 1, { ...this.selectedAddress! });
            } else {
                this.$snackbar.open({ message: 'Could not update address. Please try again.', type: 'is-danger' });
            }
        } else {
            this.partnerPractice.addresses.push({ ...this.selectedAddress! });
        }

        this.isSaving = false;
        this.onCancelAddress();
    }

    private onAddAddress(): void {
        this.selectedAddress = createPartnerPracticeAddress();
        this.isAddressVisible = true;
    }

    private onEditAddress(address: PartnerPracticeAddress): void {
        if (address.id == null) {
            address.id = 'editing';
        }

        this.selectedAddress = { ...address };
        this.isAddressEdit = true;
        this.isAddressVisible = true;
    }

    private onRemoveAddress(index: number): void {
        this.partnerPractice.addresses.splice(index, 1);
        this.onCancelAddress();
    }

    private onCancelAddress(): void {
        for (const address of this.partnerPractice.addresses.filter((a) => a.id === 'editing')) {
            address.id = null;
        }

        this.selectedAddress = null;
        this.isAddressEdit = false;
        this.isAddressVisible = false;
    }

    private async onSaveEmailAttachmentAsync(): Promise<void> {
        if (!(await (this.$refs.email as Vue).$validator.validateAll('email-attachment'))) {
            return;
        }

        this.isSaving = true;

        if (this.importEmailAttachment) {
            if (!(await this.uploadFileAsync())) {
                this.isSaving = false;
                return;
            }

            this.selectedEmailAttachment!.attachmentFilename = this.emailAttachmentFile!.name;
        }

        const extension = `.${this.selectedEmailAttachment!.attachmentFilename!.split('.').pop()}`;
        const formattedAlias = this.selectedEmailAttachment!.attachmentAlias != null && this.selectedEmailAttachment!.attachmentAlias.trim() !== ''
            ? `${this.selectedEmailAttachment!.attachmentAlias.trim()}${!this.selectedEmailAttachment!.attachmentAlias.trim().toLowerCase().endsWith(extension) ? extension : ''}`
            : null;

        if (this.isEmailEdit) {
            const index = this.partnerPractice.emailAttachments.findIndex((email) => email.id === this.selectedEmailAttachment!.id!);

            if (index > -1) {
                if (this.selectedEmailAttachment!.id === 'editing') {
                    this.selectedEmailAttachment!.id = null;
                }

                this.partnerPractice.emailAttachments.splice(index, 1, { ...this.selectedEmailAttachment!, attachmentAlias: formattedAlias });
            } else {
                this.$snackbar.open({ message: 'Could not update e-mail attachment. Please try again.', type: 'is-danger' });
            }
        } else {
            this.partnerPractice.emailAttachments.push({ ...this.selectedEmailAttachment!, attachmentAlias: formattedAlias });
        }

        await this.$store.dispatch(`attachment/fetchEmailAttachmentsAsync`);

        this.isSaving = false;
        this.onCancelEmailAttachment();
    }

    private onAddEmailAttachment(): void {
        this.selectedEmailAttachment = createPartnerPracticeEmailAttachment();
        this.importEmailAttachment = false;
        this.emailAttachmentFile = null;
        this.isEmailVisible = true;
    }

    private onEditEmailAttachment(attachment: PartnerPracticeEmailAttachment): void {
        if (attachment.id == null) {
            attachment.id = 'editing';
        }

        this.selectedEmailAttachment = { ...attachment };
        this.importEmailAttachment = false;
        this.emailAttachmentFile = null;
        this.isEmailEdit = true;
        this.isEmailVisible = true;
    }

    private onRemoveEmailAttachment(index: number): void {
        this.partnerPractice.emailAttachments.splice(index, 1);
        this.onCancelEmailAttachment();
    }

    private onCancelEmailAttachment(): void {
        for (const email of this.partnerPractice.emailAttachments.filter((e) => e.id === 'editing')) {
            email.id = null;
        }

        this.selectedEmailAttachment = null;
        this.importEmailAttachment = false;
        this.emailAttachmentFile = null;
        this.isEmailEdit = false;
        this.isEmailVisible = false;
    }

    private onImportEmailAttachmentChanged(importFile: boolean): void {
        this.importEmailAttachment = importFile;
    }

    private onEmailAttachmentFileChanged(attachmentFile: File | null): void {
        this.emailAttachmentFile = attachmentFile;
    }

    private async uploadFileAsync(): Promise<boolean> {
        try {
            await emailAttachmentApi.emailAttachmentAsync(this.emailAttachmentFile!);
            this.$snackbar.open({ message: `Email attachment '${this.emailAttachmentFile!.name}' has been uploaded.`, type: 'is-success' });

            return true;
        } catch (e) {
            if (e instanceof BadRequest) {
                this.$snackbar.open({ message: e.message, type: 'is-danger' });
            }

            this.$snackbar.open({ message: `Could not upload '${this.emailAttachmentFile!.name}'. Please try again.`, type: 'is-danger' });

            return false;
        }
    }
}
